import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, setTenant } from '../../lib/api';
import { isRootSite, tenantUrl } from '../../lib/site';
import { money, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';

interface PublicPlan {
  id: string;
  code: string;
  name: string;
  price: string;
  currency: string;
  cycle: string;
  badge: string;
  trialDays: number;
}

const EMPLOYEE_RANGES = ['1-5', '6-20', '21-50', '51-200', '200+'];

/** يقترح نطاقًا من اسم الشركة: حروف لاتينية وأرقام وشرطات. */
function suggestSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

/**
 * التسجيل الذاتي.
 *
 * خطوة واحدة لا معالج متعدد الخطوات: كل حقل هنا لازم للتهيئة، وتقسيمها على
 * شاشات يزيد نقاط الانسحاب دون أن يقلّل ما يُطلب.
 */
export function Signup() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    companyName: '',
    slug: '',
    contactName: '',
    email: '',
    phone: '',
    password: '',
    planCode: params.get('plan') ?? '',
    employeeCount: '',
    industry: '',
    message: '',
  });
  const [slugTouched, setSlugTouched] = useState(false);

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const plans = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => api<PublicPlan[]>('/public/plans', { skipAuth: true }),
  });

  useEffect(() => {
    if (!form.planCode && plans.data?.length) {
      set('planCode', plans.data.find((p) => p.badge)?.code ?? plans.data[0]!.code);
    }
  }, [plans.data, form.planCode]);

  /*
   * فحص النطاق أثناء الكتابة.
   *
   * اكتشاف أن النطاق مأخوذ بعد ملء النموذج كله وضغط الإرسال يعني إعادة
   * الإدخال. الفحص المبكر يصحّح الاختيار وهو ما زال رخيصًا.
   */
  const slugCheck = useQuery({
    queryKey: ['public', 'slug', form.slug],
    queryFn: () =>
      api<{ available: boolean; suggestion: string }>(
        `/public/slug?slug=${encodeURIComponent(form.slug)}`,
        { skipAuth: true },
      ),
    enabled: form.slug.length >= 3,
  });

  const signup = useMutation({
    mutationFn: () =>
      api<{ slug: string; trialEndsAt: string; trialDays: number }>('/public/signup', {
        method: 'POST',
        skipAuth: true,
        body: {
          companyName: form.companyName,
          contactName: form.contactName,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
          slug: form.slug,
          planCode: form.planCode,
          employeeCount: form.employeeCount || null,
          industry: form.industry || null,
          message: form.message || null,
        },
      }),
    onSuccess: (result) => {
      /*
       * حفظ النطاق يجعل الدخول التالي مباشرًا.
       *
       * في الإنتاج يُستنتج من النطاق الفرعي، لكن حفظه هنا يغني المسجِّل
       * الجديد عن كتابته في أول شاشة يراها.
       */
      setTenant(result.slug);
    },
  });

  const selected = plans.data?.find((p) => p.code === form.planCode);
  const trialDays = selected?.trialDays ?? 14;

  if (signup.isSuccess) {
    return (
      <div className="landing">
        <div className="signup-done">
          <Logo size={54} />
          <h1>شركتك جاهزة</h1>
          <p>
            بدأت فترتك التجريبية على النطاق{' '}
            <span className="num strong">{signup.data.slug}</span> ولمدة{' '}
            {plural(signup.data.trialDays, WORDS.day)}.
          </p>
          {/* الدخول على نطاق الشركة الجديد — رابطُها الدائم من الآن. */}
          {isRootSite() ? (
            <a href={tenantUrl(signup.data.slug)} className="btn btn-primary btn-lg">
              ادخل إلى حسابك
            </a>
          ) : (
            <Link to="/login" className="btn btn-primary btn-lg">
              ادخل إلى حسابك
            </Link>
          )}
          {isRootSite() && (
            <p className="small">
              رابط دخولكم الدائم:{' '}
              <a href={tenantUrl(signup.data.slug, '')} className="num" dir="ltr">
                {tenantUrl(signup.data.slug, '').replace(/^https?:\/\//, '')}
              </a>
            </p>
          )}
          <p className="tiny mute">
            سجّل الدخول ببريدك <span className="num">{form.email}</span> وكلمة المرور التي
            اخترتها.
          </p>
        </div>
      </div>
    );
  }

  const slugFree = form.slug.length < 3 || slugCheck.data?.available !== false;
  const valid =
    form.companyName.trim().length >= 2 &&
    form.slug.length >= 3 &&
    slugFree &&
    form.contactName.trim().length >= 2 &&
    /.+@.+\..+/.test(form.email) &&
    form.password.length >= 12 &&
    form.planCode;

  return (
    <div className="landing">
      <header className="landing-bar">
        <Link to="/" className="row" style={{ gap: 10, textDecoration: 'none' }}>
          <Logo size={30} />
          <span className="strong">GoyOne</span>
        </Link>
        <Link to="/login" className="btn btn-sm">
          لديّ حساب
        </Link>
      </header>

      <div className="signup-wrap">
        <div className="signup-form card">
          <div className="card-head">
            <div className="col" style={{ gap: 2 }}>
              <h1 className="card-title">ابدأ تجربتك</h1>
              <span className="tiny mute">
                {plural(trialDays, WORDS.day)} بكامل الصلاحيات، بلا بطاقة ائتمان.
              </span>
            </div>
          </div>

          <div className="card-body">
            {signup.isError && (
              <div className="alert alert-danger mb-4">
                {signup.error instanceof Error ? signup.error.message : 'تعذّر إنشاء الحساب.'}
              </div>
            )}

            <div className="form-grid">
              <div className="field span-2">
                <label className="label req" htmlFor="s-company">
                  اسم الشركة
                </label>
                <input
                  id="s-company"
                  className="input"
                  value={form.companyName}
                  onChange={(e) => {
                    set('companyName', e.target.value);
                    if (!slugTouched) set('slug', suggestSlug(e.target.value));
                  }}
                  autoFocus
                />
              </div>

              <div className="field span-2">
                <label className="label req" htmlFor="s-slug">
                  نطاق شركتك
                </label>
                <div className="row" style={{ gap: 6 }}>
                  <input
                    id="s-slug"
                    className="input num"
                    dir="ltr"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      set('slug', e.target.value.toLowerCase());
                    }}
                  />
                  <span className="num mute" dir="ltr" style={{ whiteSpace: 'nowrap' }}>
                    .goyone.com
                  </span>
                </div>
                {form.slug.length >= 3 && slugCheck.data && (
                  <span className={`tiny ${slugCheck.data.available ? 'pos' : 'neg'}`}>
                    {slugCheck.data.available
                      ? 'النطاق متاح'
                      : `النطاق مأخوذ — جرّب ${slugCheck.data.suggestion}`}
                  </span>
                )}
              </div>

              <div className="field">
                <label className="label req" htmlFor="s-name">
                  اسمك
                </label>
                <input
                  id="s-name"
                  className="input"
                  value={form.contactName}
                  onChange={(e) => set('contactName', e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label" htmlFor="s-phone">
                  رقم الهاتف
                </label>
                <input
                  id="s-phone"
                  className="input num"
                  dir="ltr"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                />
              </div>

              <div className="field span-2">
                <label className="label req" htmlFor="s-email">
                  البريد الإلكتروني
                </label>
                <input
                  id="s-email"
                  className="input num"
                  type="email"
                  dir="ltr"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                />
                <span className="tiny mute">سيكون اسم دخولك، ولن يصلك منا بريد تسويقي.</span>
              </div>

              <div className="field span-2">
                <label className="label req" htmlFor="s-password">
                  كلمة المرور
                </label>
                <input
                  id="s-password"
                  className="input"
                  type="password"
                  dir="ltr"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                />
                <span className={`tiny ${form.password && form.password.length < 12 ? 'neg' : 'mute'}`}>
                  اثنا عشر حرفًا على الأقل. حسابك يملك كل بيانات شركتك.
                </span>
              </div>

              <div className="field">
                <label className="label" htmlFor="s-size">
                  حجم الفريق
                </label>
                <select
                  id="s-size"
                  className="select"
                  value={form.employeeCount}
                  onChange={(e) => set('employeeCount', e.target.value)}
                >
                  <option value="">غير محدَّد</option>
                  {EMPLOYEE_RANGES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label className="label" htmlFor="s-industry">
                  مجال العمل
                </label>
                <input
                  id="s-industry"
                  className="input"
                  value={form.industry}
                  onChange={(e) => set('industry', e.target.value)}
                  placeholder="تسويق، مقاولات، استشارات"
                />
              </div>

              <div className="field span-2">
                <label className="label" htmlFor="s-message">
                  ما الذي تريد أن يحلّه النظام لك؟
                </label>
                <textarea
                  id="s-message"
                  className="textarea"
                  rows={2}
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                />
                <span className="tiny mute">اختياري — يساعدنا في مساعدتك أسرع.</span>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg mt-4"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => signup.mutate()}
              disabled={!valid || signup.isPending}
            >
              {signup.isPending ? 'جارٍ تهيئة شركتك' : `ابدأ ${plural(trialDays, WORDS.day)} مجانًا`}
            </button>
          </div>
        </div>

        <aside className="signup-plans">
          <h3 className="section-title">اختر باقتك</h3>
          {plans.data?.map((p) => (
            <label
              key={p.id}
              className={`pick-card ${form.planCode === p.code ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="plan"
                checked={form.planCode === p.code}
                onChange={() => set('planCode', p.code)}
              />
              <div className="col" style={{ gap: 2, flex: 1 }}>
                <div className="row" style={{ gap: 6 }}>
                  <span className="strong">{p.name}</span>
                  {p.badge && <span className="plan-badge sm">{p.badge}</span>}
                </div>
                <span className="tiny mute">
                  {p.cycle === 'YEARLY' ? 'سنويًا' : 'شهريًا'} بعد التجربة
                </span>
              </div>
              <span className="num strong">
                {money(p.price, p.currency === 'EGP' ? 'ج.م' : p.currency)}
              </span>
            </label>
          ))}

          <div className="alert alert-info mt-4">
            <Icon name="check" size={14} />
            لا تُطلب بطاقة ائتمان الآن. بعد التجربة نتواصل معك لتفعيل الاشتراك، وبياناتك تبقى
            كما هي.
          </div>
        </aside>
      </div>
    </div>
  );
}
